import React, { useState, useEffect, useMemo } from "react";
import { Plus } from "lucide-react";
import { getDataByAll } from "../../../api/workshopApi";

import SectionHeader from "../../Common/SectionHeader";
import SectionFilter from "../../Common/SectionFilter";
import SectionTable from "../../Common/SectionTable";
import Pagination from "../../Common/SectionTablePagination";

import IndexRow from "./IndexRow";
import Form from "./Form";

const WorkshopIndex = () => {
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("None");
  const itemsPerPage = 5;
  const [viewMode, setViewMode] = useState("list"); 
  const [selectedData, setSelectedData] = useState(null);

  useEffect(() => {
    setLoading(true);
    getDataByAll()
      .then((data) => setDatas(data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortColumn]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // HANDLER NAVIGASI
  const handleAddNew = () => {
    setSelectedData(null);
    setViewMode("form");
  };

  const handleEdit = (workshop) => {
    setSelectedData(workshop);
    setViewMode("form");
  };

  const handleBack = () => {
    setViewMode("list");
  };

  const handleSaveData = (data) => {
    console.log("Saving Data:", data);
    // Integrasikan dengan API .NET Anda di sini
    setViewMode("list");
  };

  // LOGIKA PENGURUTAN DAN PENYARINGAN
  const processedFilterData = useMemo(() => {
    return [...datas]
      .filter((ws) =>
        ws.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ws.speaker && ws.speaker.toLowerCase().includes(searchTerm.toLowerCase()))
      )
      .sort((a, b) => {
        if (sortColumn === "None") return 0;
        const keyMap = {
          "Workshop Title": "title",
          "Speaker": "speaker",
          "Price": "price",
          "Capacity": "quota"
        };
        const key = keyMap[sortColumn];
        const valA = (a[key] || "").toString().toLowerCase();
        const valB = (b[key] || "").toString().toLowerCase();
        return valA < valB ? -1 : 1;
      });
  }, [datas, searchTerm, sortColumn]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = processedFilterData.slice(indexOfFirstItem, indexOfLastItem);

  const tableHeaders = [
    { label: "Workshop Title" },
    { label: "Speaker" },
    { label: "Schedule & Time" },
    { label: "Price", className: "text-center" },
    { label: "Capacity", className: "text-center" },
    { label: "Actions", className: "text-center" },
  ];

  // RENDER HALAMAN FORM
  if (viewMode === "form") {
    return (
      <Form 
        onBack={handleBack} 
        onSave={handleSaveData}
        initialData={selectedData}
      />
    );
  }

  // RENDER HALAMAN TABEL
  return (
    <div className="p-6 space-y-6 bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm border border-stone-100 dark:border-white/5">
      <SectionHeader 
        title="Workshop Management"
        subtitle="Manage schedules, speakers and pricing"
        actionLabel="Add New Workshop"
        Icon={Plus}
        onActionClick={handleAddNew}
      />

      <SectionFilter 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        placeholder="Search by title or speaker..."
        columns={tableHeaders}
        selectedColumn={sortColumn}
        onColumnSelect={setSortColumn}
      />

      <SectionTable 
        headers={tableHeaders}
        data={currentItems}
        loading={loading}
        renderRow={(data) => (
          <IndexRow 
            key={data.id}
            workshop={data} 
            onEdit={handleEdit} 
          />
        )}
        emptyMessage={`No data found for "${searchTerm}"`}
      />

      <div className="pt-6 border-t border-stone-100 dark:border-white/5">
        <Pagination 
          totalItems={processedFilterData.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default WorkshopIndex;